import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allProducts } from '@/mocks/data';

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const product = allProducts.find((p) => p.slug === slug);

  if (!product) {
    const firstCat = 'vibration-clamps';
    window.location.href = `/category/${firstCat}`;
    return null;
  }

  const relatedProducts = allProducts
    .filter((p) => p.id !== product.id && p.category === product.category)
    .slice(0, 4);

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'CNY',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviewCount,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: '分类浏览', path: '/category/vibration-clamps' },
              { name: product.name },
            ]}
          />
        </div>

        <section className="py-8 md:py-12">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="aspect-square rounded-lg overflow-hidden bg-background-100">
                <img
                  src={product.image}
                  alt={product.name}
                  title={`${product.name} - 女性情趣用品推荐`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-col justify-center">
                {product.badge && (
                  <span className="inline-block bg-accent-500 text-background-50 text-xs font-medium px-3 py-1 rounded-full mb-4 w-fit">
                    {product.badge}
                  </span>
                )}
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
                  {product.name}
                </h1>

                <div className="flex items-center gap-2 mb-5">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < Math.floor(product.rating) ? 'ri-star-fill text-accent-500' : i < product.rating ? 'ri-star-half-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-sm text-foreground-600">{product.rating} 分</span>
                  <span className="text-sm text-foreground-500">({product.reviewCount} 条评价)</span>
                </div>

                <div className="text-3xl font-semibold text-primary-600 mb-6">
                  ¥{product.price}
                </div>

                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  {product.description}
                </p>

                <div className="mb-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">产品亮点</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature, i) => (
                      <span key={i} className="text-sm bg-secondary-100 text-secondary-700 px-3 py-1.5 rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <Link
                  to={`/category/${product.category}`}
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium px-8 py-3 rounded-full transition-all duration-300 whitespace-nowrap w-fit"
                >
                  浏览同类产品 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {relatedProducts.length > 0 && (
          <section className="py-12 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
                同分类推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedProducts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        title={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {p.name}
                      </h3>
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`text-[10px] ${i < Math.floor(p.rating) ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                          ></i>
                        ))}
                        <span className="text-xs text-foreground-500">({p.reviewCount})</span>
                      </div>
                      <span className="text-base font-semibold text-primary-600">¥{p.price}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}